import React, { useEffect, useState } from 'react';
import './Auth.css';
import { useToast } from '../../../components/Toast/ToastContext';
import { register, verifyMobileNumber, verifyEmail, login, sendEmailOtp, sendMobileOtp } from '../../../Actions/UserActions';
import { useNavigate } from 'react-router-dom';

const Auth = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [activeTab, setActiveTab] = useState('login');
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        mobile: '',
        emailOtp: '',
        mobileOtp: ''
    });
    const [loginType, setLoginType] = useState(null); // 'email' or 'mobile'
    const [showOtpInput, setShowOtpInput] = useState(false);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [emailOtpTimer, setEmailOtpTimer] = useState(0);
    const [mobileOtpTimer, setMobileOtpTimer] = useState(0);
    const isMobileAuthDisabled = String(process.env.REACT_APP_MOBILE_AUTH || '').toLowerCase() === 'false';
    const isEmailAuthDisabled = String(process.env.REACT_APP_EMAIL_AUTH || '').toLowerCase() === 'false';
    const loginMode = isMobileAuthDisabled && !isEmailAuthDisabled
        ? 'emailOnly'
        : isEmailAuthDisabled && !isMobileAuthDisabled
            ? 'mobileOnly'
            : (!isMobileAuthDisabled && !isEmailAuthDisabled)
                ? 'both'
                : 'emailOnly';

    const formatSeconds = (s) => `00:${String(s).padStart(2, '0')}`;

    useEffect(() => {
        if (emailOtpTimer === 0 && mobileOtpTimer === 0) return;
        const intervalId = setInterval(() => {
            setEmailOtpTimer(prev => (prev > 0 ? prev - 1 : 0));
            setMobileOtpTimer(prev => (prev > 0 ? prev - 1 : 0));
        }, 1000);
        return () => clearInterval(intervalId);
    }, [emailOtpTimer, mobileOtpTimer]);

    const validateForm = () => {
        const newErrors = {};
        
        if (!showOtpInput) {
            if (activeTab === 'signup') {
                if (!formData.full_name.trim()) {
                    newErrors.full_name = 'Full name is required';
                }
                
                if (!isEmailAuthDisabled) {
                    if (!formData.email.trim()) {
                        newErrors.email = 'Email is required';
                    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
                        newErrors.email = 'Invalid email format';
                    }
                }

                if (!isMobileAuthDisabled) {
                    if (!formData.mobile.trim()) {
                        newErrors.mobile = 'Mobile number is required';
                    } else if (!/^\d{10}$/.test(formData.mobile)) {
                        newErrors.mobile = 'Invalid mobile number format';
                    }
                }
            } else {
                // For login, enforce allowed mode based on env flags
                if (!formData.mobile.trim()) {
                    newErrors.mobile = loginMode === 'emailOnly'
                        ? 'Email is required'
                        : loginMode === 'mobileOnly'
                            ? 'Mobile number is required'
                            : 'Mobile number or Email is required';
                } else if (loginMode === 'emailOnly') {
                    if (!/\S+@\S+\.\S+/.test(formData.mobile)) {
                        newErrors.mobile = 'Invalid email format';
                    }
                } else if (loginMode === 'mobileOnly') {
                    if (!/^\d{10}$/.test(formData.mobile)) {
                        newErrors.mobile = 'Invalid mobile number format';
                    }
                } else {
                    const isEmail = formData.mobile.includes('@');
                    if (isEmail) {
                        if (!/\S+@\S+\.\S+/.test(formData.mobile)) {
                            newErrors.mobile = 'Invalid email format';
                        }
                    } else {
                        if (!/^\d{10}$/.test(formData.mobile)) {
                            newErrors.mobile = 'Invalid mobile number format';
                        }
                    }
                }
            }
        } else {
            if (activeTab === 'signup') {
                if (!formData.emailOtp.trim()) {
                    newErrors.emailOtp = 'Email OTP is required';
                } else if (!/^\d{6}$/.test(formData.emailOtp)) {
                    newErrors.emailOtp = 'Invalid Email OTP format';
                }
                if (!isMobileAuthDisabled) {
                    if (!formData.mobileOtp.trim()) {
                        newErrors.mobileOtp = 'Mobile OTP is required';
                    } else if (!/^\d{6}$/.test(formData.mobileOtp)) {
                        newErrors.mobileOtp = 'Invalid Mobile OTP format';
                    }
                }
            } else {
                // For login OTP verification
                if (!formData.mobileOtp.trim()) {
                    newErrors.mobileOtp = 'OTP is required';
                } else if (!/^\d{6}$/.test(formData.mobileOtp)) {
                    newErrors.mobileOtp = 'Invalid OTP format';
                }
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (validateForm()) {
            setIsSubmitting(true);
            try {
                if (!showOtpInput) {
                    if (activeTab === 'login') {
                        // For login, determine flow based on env
                        if (loginMode === 'emailOnly') {
                            setLoginType('email');
                            const res = await verifyEmail(formData.mobile);
                            if (res.status === 200) {
                                setShowOtpInput(true);
                                setErrors({});
                                setEmailOtpTimer(59);
                            } else {
                                showToast(res.message || 'Email verification failed. Please try again.', 'error');
                            }
                        } else if (loginMode === 'mobileOnly') {
                            setLoginType('mobile');
                            const res = await verifyMobileNumber(formData.mobile);
                            if (res.status === 200) {
                                setShowOtpInput(true);
                                setErrors({});
                                setMobileOtpTimer(59);
                            } else {
                                showToast(res.message || 'Mobile number verification failed. Please try again.', 'error');
                            }
                        } else {
                            const isEmail = formData.mobile.includes('@');
                            setLoginType(isEmail ? 'email' : 'mobile');
                            if (isEmail) {
                                const res = await verifyEmail(formData.mobile);
                                if (res.status === 200) {
                                    setShowOtpInput(true);
                                    setErrors({});
                                    setEmailOtpTimer(59);
                                } else {
                                    showToast(res.message || 'Email verification failed. Please try again.', 'error');
                                }
                            } else {
                                const res = await verifyMobileNumber(formData.mobile);
                                if (res.status === 200) {
                                    setShowOtpInput(true);
                                    setErrors({});
                                    setMobileOtpTimer(59);
                                } else {
                                    showToast(res.message || 'Mobile number verification failed. Please try again.', 'error');
                                }
                            }
                        }
                    } else {
                        // For signup, send OTPs based on env flag
                        if (loginMode === 'emailOnly') {
                            const emailRes = await sendEmailOtp(formData.email);
                            if (emailRes?.status === 200) {
                                setShowOtpInput(true);
                                setErrors({});
                                showToast('Email OTP sent successfully! Please verify your email.', 'success');
                                setEmailOtpTimer(59);
                            } else {
                                const msg = emailRes?.data?.message || 'Failed to send Email OTP. Please try again.';
                                showToast(msg, 'error');
                            }
                        } else if (loginMode === 'mobileOnly') {
                            const mobileRes = await sendMobileOtp(formData.mobile);
                            if (mobileRes?.status === 200) {
                                setShowOtpInput(true);
                                setErrors({});
                                showToast('Mobile OTP sent successfully! Please verify your mobile number.', 'success');
                                setMobileOtpTimer(59);
                            } else {
                                const msg = mobileRes?.data?.message || 'Failed to send Mobile OTP. Please try again.';
                                showToast(msg, 'error');
                            }
                        } else {
                            const [emailRes, mobileRes] = await Promise.all([
                                sendEmailOtp(formData.email),
                                sendMobileOtp(formData.mobile)
                            ]);
                            if (emailRes?.status === 200 && mobileRes?.status === 200) {
                                setShowOtpInput(true);
                                setErrors({});
                                showToast('OTPs sent successfully! Please verify your email and mobile number.', 'success');
                                setEmailOtpTimer(59);
                                setMobileOtpTimer(59);
                            } else {
                                const msg = emailRes?.data?.message || mobileRes?.data?.message || 'Failed to send OTPs. Please try again.';
                                showToast(msg, 'error');
                            }
                        }
                    }
                } else {
                    // Verify OTP for both login and signup
                    if (activeTab === 'signup') {
                        // For signup, submit registration with OTPs based on env flag
                        let payload = loginMode === 'emailOnly'
                            ? {
                                full_name: formData.full_name,
                                email: formData.email,
                                emailOtp: formData.emailOtp
                              }
                            : loginMode === 'mobileOnly'
                                ? {
                                    full_name: formData.full_name,
                                    mobile: formData.mobile,
                                    mobileOtp: formData.mobileOtp
                                  }
                                : {
                                    full_name: formData.full_name,
                                    email: formData.email,
                                    mobile: formData.mobile,
                                    emailOtp: formData.emailOtp,
                                    mobileOtp: formData.mobileOtp
                                  };
                        // If mobile auth is disabled via env, ensure mobile fields are empty strings
                        if (isMobileAuthDisabled) {
                            payload = {
                                ...payload,
                                mobile: '',
                                mobileOtp: ''
                            };
                        }
                        const res = await register(payload);
                        if (res.success) {
                            handleSuccessfulAuth(res);
                        } else {
                            showToast(res?.message || 'Registration failed. Please try again.', 'error');
                        }
                    } else {
                        // For login, use the unified login API
                        const res = await login(formData.mobile, formData.mobileOtp, loginType === 'email');
                        
                        if (res) {
                            handleSuccessfulAuth(res);
                        } else {
                            showToast(res.message || 'Invalid OTP. Please try again.', 'error');
                        }
                    }
                }
            } catch (error) {
                console.error('Error:', error);
                showToast('An error occurred. Please try again.', 'error');
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleResendEmailOtp = async () => {
        if (emailOtpTimer > 0) return;
        try {
            const emailToUse = activeTab === 'login' ? formData.mobile : formData.email;
            const res = await sendEmailOtp(emailToUse);
            if (res?.status === 200) {
                setEmailOtpTimer(59);
                showToast('Email OTP resent.', 'success');
            } else {
                const msg = res?.data?.message || 'Failed to resend Email OTP.';
                showToast(msg, 'error');
            }
        } catch (e) {
            showToast('Failed to resend Email OTP.', 'error');
        }
    };

    const handleResendMobileOtp = async () => {
        if (mobileOtpTimer > 0) return;
        try {
            const res = await sendMobileOtp(formData.mobile);
            if (res?.status === 200) {
                setMobileOtpTimer(59);
                showToast('Mobile OTP resent.', 'success');
            } else {
                const msg = res?.data?.message || 'Failed to resend Mobile OTP.';
                showToast(msg, 'error');
            }
        } catch (e) {
            showToast('Failed to resend Mobile OTP.', 'error');
        }
    };

    const handleEditFields = () => {
        // Re-enable editing and hide OTP inputs to avoid validation errors
        setShowOtpInput(false);
        setLoginType(null);
        setFormData(prev => ({ ...prev, emailOtp: '', mobileOtp: '' }));
        setErrors(prev => ({ ...prev, emailOtp: '', mobileOtp: '' }));
        setEmailOtpTimer(0);
        setMobileOtpTimer(0);
    };

    const handleSwitchTab = (tab) => {
        // Reset the entire form when switching tabs
        setActiveTab(tab);
        setShowOtpInput(false);
        setLoginType(null);
        setFormData({
            full_name: '',
            email: '',
            mobile: '',
            emailOtp: '',
            mobileOtp: ''
        });
        setErrors({});
        setIsSubmitting(false);
        setEmailOtpTimer(0);
        setMobileOtpTimer(0);
    };

    const handleSuccessfulAuth = (res) => {
        const token = res?.data?.token;
        const userData = res?.data?.data;
        
        if (token && userData) {
            localStorage.setItem('access_token', token);
            localStorage.setItem('user_data', JSON.stringify(userData));
            localStorage.setItem('userType', userData.type);
            
            // Navigate based on user type
            if (userData.type === 'admin') {
                navigate('/admin/users');
            } else {
                navigate('/user/form');
            }
            
            // Reset form
            setFormData({
                full_name: '',
                email: '',
                mobile: '',
                emailOtp: '',
                mobileOtp: ''
            });
            setShowOtpInput(false);
            setLoginType(null);
            setEmailOtpTimer(0);
            setMobileOtpTimer(0);
            
            showToast('Successfully logged in!', 'success');
        } else {
            showToast('Authentication failed. Please try again.', 'error');
        }
    };

    return (
        <div className="auth-container h-full px-4 scroll-enabled">
            <div className="auth-card">
                <div className='flex items-center justify-center cursor-pointer' onClick={() => navigate("/")}>
                    <img onClick={() => navigate("/")} src="/Assets/Images/logo.svg" alt="Project Sunflower Lanyard logo with a bright yellow half sunflower icon next to the text, on a purple background." className="w-[6rem]" />
                </div>
                {activeTab === 'login' && <p className="auth-subtitle mt-8">{
                    loginMode === 'emailOnly'
                        ? 'Please enter email to login'
                        : loginMode === 'mobileOnly'
                            ? 'Please enter mobile number to login'
                            : 'Please enter mobile number or email to login'
                }</p>}
                {activeTab === 'signup' && <p className="auth-subtitle mt-8">Create a profile to apply for your own Disability card</p>}
                <div className="auth-tab-container">
                    <button
                        className={`auth-tab ${activeTab === 'login' ? 'active' : ''}`}
                        onClick={() => handleSwitchTab('login')}
                    >
                        Login
                    </button>
                    <button
                        className={`auth-tab ${activeTab === 'signup' ? 'active' : ''}`}
                        onClick={() => handleSwitchTab('signup')}
                    >
                        Sign Up
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    {activeTab === 'signup' && (
                        <div className="form-group">
                            <label className="form-label">Full Name</label>
                            <input
                                className="form-input"
                                type="text"
                                name="full_name"
                                placeholder="John Doe"
                                value={formData.full_name}
                                onChange={handleInputChange}
                            />
                            {errors.full_name && <span className="error-text">{errors.full_name}</span>}
                        </div>
                    )}
                    
                    {activeTab === 'signup' && !isEmailAuthDisabled && (
                        <div className="form-group">
                            <div className="flex items-center justify-between">
                                <label className="form-label">Email</label>
                                {showOtpInput && (
                                    <button type="button" className="flex items-center gap-1 text-sm text-[#753ABF] hover:underline" onClick={handleEditFields}>
                                        <img src="/Assets/Images/editFieldIcon.svg" alt="Edit" className="h-4 w-4" />
                                        Edit
                                    </button>
                                )}
                            </div>
                            <input
                                className="form-input"
                                type="email"
                                name="email"
                                placeholder="john.doe@example.com"
                                value={formData.email}
                                onChange={handleInputChange}
                                disabled={showOtpInput}
                            />
                            {errors.email && <span className="error-text">{errors.email}</span>}
                        </div>
                    )}

                    {activeTab === 'signup' && !isMobileAuthDisabled && <div className="form-group">
                        <div className="flex items-center justify-between">
                            <label className="form-label">Mobile Number</label>
                            {showOtpInput && (
                                <button type="button" className="flex items-center gap-1 text-sm text-[#753ABF] hover:underline" onClick={handleEditFields}>
                                    <img src="/Assets/Images/editFieldIcon.svg" alt="Edit" className="h-4 w-4" />
                                    Edit
                                </button>
                            )}
                        </div>
                        <div className={`phone-input-group${showOtpInput ? ' disabled' : ''}`}>
                            <span className="phone-prefix">🇮🇳 +91</span>
                            <input
                                className="phone-input"
                                type="tel"
                                name="mobile"
                                placeholder="9876543210"
                                value={formData.mobile}
                                onChange={handleInputChange}
                                maxLength={10}
                                disabled={showOtpInput}
                            />
                        </div>
                        {errors.mobile && <span className="error-text">{errors.mobile}</span>}
                    </div>}

                    {activeTab === 'login' && <div className="form-group">
                        <div className="flex items-center justify-between">
                            <label className="form-label">{loginMode === 'emailOnly' ? 'Email' : loginMode === 'mobileOnly' ? 'Mobile Number' : 'Mobile Number / Email'}</label>
                            {showOtpInput && (
                                <button type="button" className="flex items-center gap-1 text-sm text-[#753ABF] hover:underline" onClick={handleEditFields}>
                                    <img src="/Assets/Images/editFieldIcon.svg" alt="Edit" className="h-4 w-4" />
                                    Edit
                                </button>
                            )}
                        </div>
                        <div className={`phone-input-group${showOtpInput ? ' disabled' : ''}`}>
                            <input
                                className="phone-input"
                                type={loginMode === 'emailOnly' ? 'email' : loginMode === 'mobileOnly' ? 'tel' : 'text'}
                                name="mobile"
                                placeholder={loginMode === 'emailOnly' ? 'Enter your Email' : loginMode === 'mobileOnly' ? 'Enter your Mobile number' : 'Enter your Mobile number / Email'}
                                value={formData.mobile}
                                onChange={handleInputChange}
                                disabled={showOtpInput}
                                maxLength={loginMode === 'mobileOnly' ? 10 : undefined}
                            />
                        </div>
                        {errors.mobile && <span className="error-text">{errors.mobile}</span>}
                    </div>}

                    {showOtpInput && activeTab === 'signup' && (
                        <>
                            {!isEmailAuthDisabled && (
                                <div className="form-group">
                                    <div className="flex items-center justify-between">
                                        <label className="form-label">Enter Email OTP</label>
                                        {emailOtpTimer > 0 ? (
                                            <span className="text-sm text-gray-600">Resend in {formatSeconds(emailOtpTimer)}</span>
                                        ) : (
                                            <button type="button" className="text-sm text-[#753ABF] hover:underline" onClick={handleResendEmailOtp}>
                                                Resend OTP
                                            </button>
                                        )}
                                    </div>
                                    <input
                                        className="form-input"
                                        type="text"
                                        name="emailOtp"
                                        placeholder="Enter Email OTP"
                                        value={formData.emailOtp}
                                        onChange={handleInputChange}
                                        maxLength={6}
                                    />
                                    {errors.emailOtp && <span className="error-text">{errors.emailOtp}</span>}
                                </div>
                            )}
                            {!isMobileAuthDisabled && (
                                <div className="form-group">
                                    <div className="flex items-center justify-between">
                                        <label className="form-label">Enter Mobile OTP</label>
                                        {mobileOtpTimer > 0 ? (
                                            <span className="text-sm text-gray-600">Resend in {formatSeconds(mobileOtpTimer)}</span>
                                        ) : (
                                            <button type="button" className="text-sm text-[#753ABF] hover:underline" onClick={handleResendMobileOtp}>
                                                Resend OTP
                                            </button>
                                        )}
                                    </div>
                                    <input
                                        className="form-input"
                                        type="text"
                                        name="mobileOtp"
                                        placeholder="Enter Mobile OTP"
                                        value={formData.mobileOtp}
                                        onChange={handleInputChange}
                                        maxLength={6}
                                    />
                                    {errors.mobileOtp && <span className="error-text">{errors.mobileOtp}</span>}
                                </div>
                            )}
                        </>
                    )}
                    {showOtpInput && activeTab === 'login' && (
                        <div className="form-group">
                            <div className="flex items-center justify-between">
                                <label className="form-label">
                                    {loginType === 'email' ? 'Enter Email OTP' : 'Enter Mobile OTP'}
                                </label>
                                {loginType === 'email' ? (
                                    emailOtpTimer > 0 ? (
                                        <span className="text-sm text-gray-600">Resend in {formatSeconds(emailOtpTimer)}</span>
                                    ) : (
                                        <button type="button" className="text-sm text-[#753ABF] hover:underline" onClick={handleResendEmailOtp}>
                                            Resend OTP
                                        </button>
                                    )
                                ) : (
                                    mobileOtpTimer > 0 ? (
                                        <span className="text-sm text-gray-600">Resend in {formatSeconds(mobileOtpTimer)}</span>
                                    ) : (
                                        <button type="button" className="text-sm text-[#753ABF] hover:underline" onClick={handleResendMobileOtp}>
                                            Resend OTP
                                        </button>
                                    )
                                )}
                            </div>
                            <input
                                className="form-input"
                                type="text"
                                name="mobileOtp"
                                placeholder={loginType === 'email' ? 'Enter Email OTP' : 'Enter Mobile OTP'}
                                value={formData.mobileOtp}
                                onChange={handleInputChange}
                                maxLength={6}
                            />
                            {errors.mobileOtp && <span className="error-text">{errors.mobileOtp}</span>}
                        </div>
                    )}

                    <button 
                        className="submit-button"
                        type="submit"
                        disabled={
                            isSubmitting ||
                            (showOtpInput && activeTab === 'signup' && (
                                (loginMode === 'emailOnly' && !/^\d{6}$/.test(formData.emailOtp)) ||
                                (loginMode === 'mobileOnly' && !/^\d{6}$/.test(formData.mobileOtp)) ||
                                (loginMode === 'both' && (!/^\d{6}$/.test(formData.emailOtp) || !/^\d{6}$/.test(formData.mobileOtp)))
                            ))
                        }
                    >
                        {isSubmitting 
                            ? (showOtpInput ? 'Verifying OTP...' : 'Sending OTP...') 
                            : (showOtpInput ? 'Verify OTP' : 'Send OTP')
                        }
                    </button>
                </form>
            </div>
        </div>
    );  
};

export default Auth;